import './styles.css';
import packageInfo from '../package.json';
import type { GameMode, PlayerColor, ScoreEntry, ServerMessage } from '../shared/protocol';
import {
  Game,
  type DamageDirection,
  type GameResult,
  type GameSettings,
  type HudState,
  type MinimapState,
} from './game/Game';
import { NetworkClient } from './game/multiplayer';
import { PLAYER_SUIT_LABELS } from './game/PlayerModel';
import { SuitPreview } from './SuitPreview';

const $ = <T extends HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Elemento mancante: ${selector}`);
  return element;
};

const screens = [...document.querySelectorAll<HTMLElement>('.screen')];
const canvas = $('#game') as HTMLCanvasElement;
const hud = $('#hud');
const toast = $('#toast');
const damageFlash = $('#damage-flash');
const hitMarker = $('#hit-marker');
const damageDirection = $('#damage-direction');
const suitStatus = $('#suit-status');
const levelCard = $('#level-card');
const minimapCanvas = $('#minimap') as HTMLCanvasElement;
const minimapContext = minimapCanvas.getContext('2d');
const minimapBase = document.createElement('canvas');
minimapBase.width = minimapCanvas.width;
minimapBase.height = minimapCanvas.height;
const minimapBaseContext = minimapBase.getContext('2d');
let minimapBaseSignature = '';
let toastTimer = 0;
let levelTimer = 0;
let damageTimer = 0;
let directionTimer = 0;
let visorHitTimer = 0;
let network: NetworkClient | undefined;
let suitPreview: SuitPreview | undefined;
let lastMode: 'solo' | 'online' = 'solo';
let lastPlayerCount = 1;
const RECORD_VERSION = 'v4-foundry';
const PLAYER_COLORS: Record<PlayerColor, string> = {
  cyan: '#58c9d4',
  orange: '#e46628',
  violet: '#895fd9',
  green: '#70c987',
};
let selectedOnlineMode: GameMode = 'campaign';
let lastGameMode: GameMode = 'campaign';
let latestLeaderboard: ScoreEntry[] = [];

const defaultSettings: GameSettings = { sensitivity: 1, volume: 0.7, reducedMotion: false, difficulty: 'normal' };
const loadSettings = (): GameSettings => {
  try {
    return { ...defaultSettings, ...JSON.parse(localStorage.getItem('qod-settings') || '{}') };
  } catch {
    return defaultSettings;
  }
};
let settings = loadSettings();

const showScreen = (id?: string): void => {
  for (const screen of screens) screen.classList.toggle('is-active', screen.id === id);
  suitPreview?.setActive(id === 'online-screen');
};

const showToast = (message: string): void => {
  window.clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.add('is-visible');
  toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
};

const showGame = (): void => {
  showScreen();
  const suitColor = lastMode === 'online'
    ? ($('#player-color') as HTMLSelectElement).value as PlayerColor
    : 'orange';
  hud.style.setProperty('--suit-accent', PLAYER_COLORS[suitColor]);
  hud.hidden = false;
};

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, '0')}`;
};

const loadAchievements = (): Set<string> => {
  try {
    const stored = JSON.parse(localStorage.getItem('qod-achievements') || '[]') as unknown;
    return new Set(Array.isArray(stored) ? stored.filter((item): item is string => typeof item === 'string') : []);
  } catch {
    return new Set();
  }
};

const drawMinimap = (state?: MinimapState): void => {
  if (!minimapContext || !minimapBaseContext) return;
  const output = minimapContext;
  const canvasWidth = minimapCanvas.width;
  const canvasHeight = minimapCanvas.height;
  output.imageSmoothingEnabled = false;
  output.clearRect(0, 0, canvasWidth, canvasHeight);
  output.fillStyle = '#071013';
  output.fillRect(0, 0, canvasWidth, canvasHeight);
  if (!state) return;

  const rows = state.grid.length;
  const cols = state.grid[0]?.length ?? 0;
  if (!rows || !cols) return;
  const padding = 12;
  const cellSize = Math.min((canvasWidth - padding * 2) / cols, (canvasHeight - padding * 2) / rows);
  const offsetX = (canvasWidth - cols * cellSize) / 2;
  const offsetY = (canvasHeight - rows * cellSize) / 2;
  const cellCenter = (row: number, col: number): [number, number] => [
    offsetX + (col + 0.5) * cellSize,
    offsetY + (row + 0.5) * cellSize,
  ];
  const signalColors = { yellow: '#f5cc28', cyan: '#58c9d4', red: '#db5743' } as const;
  const signature = [
    state.revision,
    rows,
    cols,
    ...state.doors.map((door) => `${door.row},${door.col},${door.color},${Number(door.open)}`),
    ...state.secrets.map((secret) => `${secret.row},${secret.col},${Number(secret.open)}`),
    ...state.keys.map((key) => `${key.row},${key.col},${key.color}`),
  ].join('|');

  if (signature !== minimapBaseSignature) {
    minimapBaseSignature = signature;
    const ctx = minimapBaseContext;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);
    ctx.fillStyle = '#071013';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        if (!state.explored[row * cols + col]) continue;
        const cell = state.grid[row][col];
        const secret = cell === '?' ? state.secrets.find((candidate) => candidate.row === row && candidate.col === col) : undefined;
        ctx.fillStyle = cell === '#' || (secret && !secret.open) ? '#4a5758' : '#173033';
        ctx.fillRect(offsetX + col * cellSize, offsetY + row * cellSize, Math.ceil(cellSize), Math.ceil(cellSize));
        if (cell !== '#') {
          ctx.strokeStyle = 'rgba(110, 218, 220, 0.09)';
          ctx.lineWidth = 1;
          ctx.strokeRect(offsetX + col * cellSize, offsetY + row * cellSize, cellSize, cellSize);
        }
        if (cell === 'X') {
          const [x, y] = cellCenter(row, col);
          ctx.strokeStyle = '#65e1dc';
          ctx.lineWidth = Math.max(2, cellSize * 0.22);
          ctx.beginPath();
          ctx.arc(x, y, cellSize * 0.34, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }
    for (const door of state.doors) {
      if (!state.explored[door.row * cols + door.col]) continue;
      const [x, y] = cellCenter(door.row, door.col);
      ctx.fillStyle = door.open ? '#244447' : signalColors[door.color];
      ctx.fillRect(x - cellSize * 0.34, y - cellSize * 0.34, cellSize * 0.68, cellSize * 0.68);
    }
    for (const key of state.keys) {
      if (!state.explored[key.row * cols + key.col]) continue;
      const [x, y] = cellCenter(key.row, key.col);
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI / 4);
      ctx.fillStyle = signalColors[key.color];
      ctx.fillRect(-cellSize * 0.28, -cellSize * 0.28, cellSize * 0.56, cellSize * 0.56);
      ctx.restore();
    }
  }
  output.drawImage(minimapBase, 0, 0);
  const ctx = output;
  for (const teammate of state.teammates) {
    if (!state.explored[teammate.row * cols + teammate.col]) continue;
    const [x, y] = cellCenter(teammate.row, teammate.col);
    ctx.fillStyle = PLAYER_COLORS[teammate.color] ?? PLAYER_COLORS.cyan;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(3, cellSize * 0.32), 0, Math.PI * 2);
    ctx.fill();
  }

  const [playerX, playerY] = cellCenter(state.player.row, state.player.col);
  ctx.save();
  ctx.translate(playerX, playerY);
  ctx.rotate(-state.player.yaw);
  ctx.fillStyle = '#ffcf28';
  ctx.strokeStyle = '#111719';
  ctx.lineWidth = Math.max(2, cellSize * 0.18);
  ctx.beginPath();
  ctx.moveTo(0, -cellSize * 0.82);
  ctx.lineTo(cellSize * 0.55, cellSize * 0.58);
  ctx.lineTo(0, cellSize * 0.32);
  ctx.lineTo(-cellSize * 0.55, cellSize * 0.58);
  ctx.closePath();
  ctx.stroke();
  ctx.fill();
  ctx.restore();
};

const updateHud = (state: HudState): void => {
  $('#health').textContent = String(state.health);
  $('#health').style.color = state.health <= 30 ? 'var(--danger)' : 'var(--success)';
  $('#health-fill').style.transform = `scaleX(${state.health / 100})`;
  $('#health-meter').setAttribute('aria-valuenow', String(state.health));
  $('#retro-console').dataset.health = state.health <= 30 ? 'critical' : 'stable';
  $('#weapon-name').textContent = state.weapon;
  $('#ammo').textContent = state.ammo;
  $('#run-time').textContent = formatTime(state.elapsedSeconds);
  $('#run-score').textContent = state.score.toLocaleString('it-IT');
  const stamina = Math.round(state.stamina);
  $('#stamina-fill').style.transform = `scaleX(${stamina / 100})`;
  $('#stamina-meter').setAttribute('aria-valuenow', String(stamina));
  const movementLabels: Record<HudState['movementState'], string> = {
    ready: 'PRONTO',
    sprinting: 'SCATTO!',
    tired: 'FIATO CORTO',
    slowed: 'SAPONE!',
  };
  $('#movement-status').textContent = movementLabels[state.movementState];
  $('#mobility-readout').dataset.state = state.movementState;
  $('#reload-status').textContent = state.reloading ? 'RICARICA…' : 'R · RICARICA';
  $('#reload-status').classList.toggle('is-reloading', state.reloading);
  const visorState = state.health <= 0
    ? 'downed'
    : state.health <= 30
      ? 'critical'
      : state.movementState === 'slowed'
        ? 'soapy'
        : state.reloading
          ? 'reloading'
          : state.movementState === 'sprinting'
            ? 'sprinting'
            : state.movementState === 'tired'
              ? 'tired'
              : 'ready';
  const visorLabels: Record<string, string> = {
    ready: 'TUTA INTEGRA',
    sprinting: 'PRESSIONE MASSIMA',
    tired: 'PRESSIONE BASSA',
    soapy: 'SAPONE NELLA VISIERA',
    reloading: 'RICARICA IN CORSO',
    critical: 'INTEGRITÀ CRITICA',
    downed: 'UNITÀ A TERRA',
  };
  suitStatus.dataset.state = visorState;
  $('#suit-status-label').textContent = visorLabels[visorState];
  suitStatus.setAttribute('aria-label', visorLabels[visorState]);
  $('#ducks-left').textContent = String(state.ducksLeft);
  $('#objective').textContent = state.objective;
  $('#map-label').textContent = state.mode === 'horde' ? `CAMERA DELLE BOLLE · ONDATA ${state.wave}` : `QUADRO ${state.mapIndex + 1} / 3`;
  const squadPanel = $('#squad-panel');
  squadPanel.hidden = state.teammates.length === 0;
  $('#squad-list').replaceChildren(...state.teammates.map((teammate) => {
    const item = document.createElement('li');
    item.dataset.color = teammate.color;
    const name = document.createElement('span');
    name.textContent = teammate.nickname;
    const health = document.createElement('strong');
    health.textContent = teammate.health <= 0 ? 'A TERRA' : teammate.paused ? 'PAUSA' : `${Math.round(teammate.health)} HP`;
    const meter = document.createElement('i');
    meter.style.setProperty('--squad-health', `${Math.max(0, teammate.health)}%`);
    item.append(name, health, meter);
    return item;
  }));
  drawMinimap(state.minimap);
  const keys = $('#keys');
  const keyLabels = { yellow: 'G', cyan: 'C', red: 'R' } as const;
  const keyNames = { yellow: 'gialla', cyan: 'ciano', red: 'rossa' } as const;
  const keyChips: HTMLElement[] = state.keys.map((key) => {
    const chip = document.createElement('span');
    chip.className = `key-chip key-${key}`;
    chip.textContent = keyLabels[key];
    chip.title = `Chiave ${keyNames[key]}`;
    return chip;
  });
  if (keyChips.length === 0) {
    const empty = document.createElement('small');
    empty.textContent = 'NESSUNA';
    keyChips.push(empty);
  }
  keys.replaceChildren(...keyChips);

  const prompt = $('#interaction-prompt');
  prompt.hidden = !state.interaction;
  prompt.classList.toggle('is-blocked', Boolean(state.interaction?.blocked));
  if (state.interaction) {
    const interactionKey = $('#interaction-key');
    interactionKey.hidden = !state.interaction.key;
    interactionKey.textContent = state.interaction.key ?? '';
    $('#interaction-copy').textContent = state.interaction.label;
  }

  const bossBar = $('#boss-bar');
  bossBar.hidden = !state.boss;
  if (state.boss) {
    const percentage = Math.max(0, Math.round((state.boss.health / state.boss.maxHealth) * 100));
    $('#boss-name').textContent = state.boss.label;
    $('#boss-health-value').textContent = `${percentage}%`;
    $('#boss-health-fill').style.transform = `scaleX(${percentage / 100})`;
    $('#boss-health-meter').setAttribute('aria-valuenow', String(percentage));
  }
};

const onResult = (result: GameResult): void => {
  hud.hidden = true;
  showScreen('result-screen');
  $('#result-kicker').textContent = result.victory ? 'MISSIONE COMPIUTA' : 'PAPERELLA DOMINANTE';
  $('#result-title').textContent = result.victory ? 'Vasca bonificata!' : 'Sei andato a fondo';
  const recordKey = `qod-best-${RECORD_VERSION}-${lastMode}-${lastPlayerCount}p`;
  const previous = Number(localStorage.getItem(recordKey) || Number.POSITIVE_INFINITY);
  if (result.victory && result.elapsedSeconds < previous) localStorage.setItem(recordKey, String(result.elapsedSeconds));
  if (result.victory) {
    localStorage.setItem('qod-campaign-complete', '1');
    refreshHordeUnlock();
  }
  const record = result.victory ? Math.min(previous, result.elapsedSeconds) : previous;
  $('#result-copy').textContent = result.victory
    ? `Tempo ${formatTime(result.elapsedSeconds)} · ${result.kills} paperelle sgonfiate · Record ${formatTime(record)}`
    : result.mode === 'horde'
      ? `Hai difeso la pompa fino all’ondata ${result.wave}. Le valvole chiedono la rivincita.`
      : `Hai resistito ${formatTime(result.elapsedSeconds)}. Le paperelle chiedono la rivincita.`;
  const resultStats = $('#result-stats');
  const stats: Array<[string, string]> = [
    ['Punteggio', result.score.toLocaleString('it-IT')],
    ['Paperelle', String(result.kills)],
    ['Segreti', String(result.secrets)],
    ['Rianimazioni', String(result.revives)],
    ['Danni subiti', String(result.damageTaken)],
  ];
  resultStats.replaceChildren(...stats.flatMap(([label, value]) => {
    const term = document.createElement('dt');
    term.textContent = label;
    const description = document.createElement('dd');
    description.textContent = value;
    return [term, description];
  }));
  const achievements = [
    result.victory && result.mode === 'campaign' ? 'Nessuna papera lasciata gonfia' : '',
    result.secrets >= 6 ? 'Idraulico complottista · 6 segreti' : '',
    result.damageTaken === 0 && result.victory ? 'Impermeabile · zero danni' : '',
    result.difficulty === 'nightmare' && result.victory ? 'Quackmare domata' : '',
    result.revives > 0 ? 'Nessun paperotto indietro' : '',
    result.mode === 'horde' && result.wave >= 10 ? 'Guardiano della pompa' : '',
  ].filter(Boolean);
  const storedAchievements = loadAchievements();
  achievements.forEach((achievement) => storedAchievements.add(achievement));
  localStorage.setItem('qod-achievements', JSON.stringify([...storedAchievements]));
  $('#achievement-list').replaceChildren(...achievements.map((achievement) => {
    const item = document.createElement('li');
    item.textContent = `OBIETTIVO · ${achievement}`;
    return item;
  }));
  if (lastMode === 'online' && network?.connected && network.isHost) {
    network.submitScore({
      score: result.score,
      mode: result.mode,
      difficulty: result.difficulty,
      elapsedSeconds: result.elapsedSeconds,
      kills: result.kills,
      wave: result.wave,
    });
  }
  $('#restart-btn').textContent = lastMode === 'online' && !network?.isHost ? "Attendi l'host" : 'Rigioca';
  ($('#restart-btn') as HTMLButtonElement).disabled = lastMode === 'online' && !network?.isHost;
};

const game = new Game(canvas, {
  onHud: updateHud,
  onToast: showToast,
  onLevel: (level, index) => {
    window.clearTimeout(levelTimer);
    $('#level-number').textContent = level.id === 'camera-bolle' ? 'MODALITÀ ORDE' : `QUADRO ${index + 1} / 3`;
    $('#level-name').textContent = level.name;
    $('#level-subtitle').textContent = level.subtitle;
    levelCard.hidden = false;
    levelTimer = window.setTimeout(() => { levelCard.hidden = true; }, 2700);
  },
  onDamage: (direction: DamageDirection) => {
    window.clearTimeout(damageTimer);
    window.clearTimeout(directionTimer);
    window.clearTimeout(visorHitTimer);
    damageFlash.classList.add('is-active');
    damageDirection.dataset.direction = direction;
    damageDirection.classList.add('is-active');
    suitStatus.classList.add('is-hit');
    damageTimer = window.setTimeout(() => damageFlash.classList.remove('is-active'), 130);
    directionTimer = window.setTimeout(() => damageDirection.classList.remove('is-active'), 420);
    visorHitTimer = window.setTimeout(() => suitStatus.classList.remove('is-hit'), 180);
  },
  onHit: () => {
    hitMarker.classList.add('is-active');
    window.setTimeout(() => hitMarker.classList.remove('is-active'), 90);
  },
  onPause: (actuallyPaused) => {
    showScreen('pause-screen');
    $('#pause-note').textContent = actuallyPaused
      ? 'La partita è in pausa.'
      : 'La cooperativa continua, ma le paperelle non possono colpirti finché sei qui.';
  },
  onResult,
}, settings);

const updateLobby = (): void => {
  if (!network) return;
  $('#lobby-code').textContent = network.roomCode || '-----';
  const inviteUrl = new URL(location.origin);
  inviteUrl.searchParams.set('room', network.roomCode);
  ($('#invite-link') as HTMLInputElement).value = inviteUrl.toString();
  const list = $('#player-list');
  list.replaceChildren(...network.players.map((player) => {
    const item = document.createElement('li');
    const dot = document.createElement('span');
    dot.className = 'player-dot';
    dot.dataset.color = player.color;
    const name = document.createElement('strong');
    name.textContent = player.nickname;
    item.append(dot, name);
    if (player.isHost) {
      const host = document.createElement('span');
      host.className = 'host-tag';
      host.textContent = 'HOST';
      item.append(host);
    }
    if (!player.connected) {
      const reconnecting = document.createElement('span');
      reconnecting.className = 'reconnect-tag';
      reconnecting.textContent = 'RIENTRA…';
      item.append(reconnecting);
    }
    return item;
  }));
  ($('#start-online-btn') as HTMLButtonElement).hidden = !network.isHost;
  $('#lobby-waiting').hidden = network.isHost;
};

const renderLeaderboard = (): void => {
  const list = $('#leaderboard-list');
  if (latestLeaderboard.length === 0) {
    const empty = document.createElement('li');
    empty.textContent = 'Nessun punteggio ancora: la vasca aspetta il primo temerario.';
    list.replaceChildren(empty);
    return;
  }
  list.replaceChildren(...latestLeaderboard.map((entry, index) => {
    const item = document.createElement('li');
    const rank = document.createElement('strong');
    rank.textContent = String(index + 1).padStart(2, '0');
    const player = document.createElement('span');
    player.textContent = entry.nickname;
    const detail = document.createElement('small');
    detail.textContent = entry.mode === 'horde'
      ? `ONDATA ${entry.wave} · ${entry.kills} PAPERE`
      : `${formatTime(entry.elapsedSeconds)} · ${entry.kills} PAPERE`;
    const score = document.createElement('b');
    score.textContent = entry.score.toLocaleString('it-IT');
    item.append(rank, player, detail, score);
    return item;
  }));
};

const bindNetwork = (client: NetworkClient): void => {
  client.on<Extract<ServerMessage, { type: 'room-state' }>>('room-state', (message) => {
    if (message.started) {
      $('#network-pill').textContent = `● ${client.roomCode} · ${message.players.filter((player) => player.connected).length}/${message.players.length} CONNESSI`;
      return;
    }
    showScreen('lobby-screen');
    updateLobby();
  });
  client.on<Extract<ServerMessage, { type: 'game-start' }>>('game-start', (message) => {
    lastMode = 'online';
    lastPlayerCount = client.players.length;
    lastGameMode = message.mode;
    settings = { ...settings, difficulty: message.difficulty };
    game.updateSettings(settings);
    showGame();
    $('#network-pill').hidden = false;
    $('#network-pill').textContent = `● ${client.roomCode} · ${lastPlayerCount} GIOCATORI`;
    game.startOnline(client, message.mode);
    showToast(client.legacyProtocol
      ? 'Server VPS precedente rilevato: compatibilità attiva. Aggiorna il backend per colori e difficoltà condivisi.'
      : message.reconnected
        ? 'Sessione recuperata. Di nuovo nella vasca!'
        : 'Click nel gioco per agganciare il mouse. Esc apre il pannello.');
  });
  client.on<Extract<ServerMessage, { type: 'error' }>>('error', (message) => {
    $('#online-error').textContent = message.message;
    showToast(message.message);
  });
  client.on<Extract<ServerMessage, { type: 'leaderboard' }>>('leaderboard', (message) => {
    latestLeaderboard = message.entries;
    renderLeaderboard();
  });
  client.on<Extract<ServerMessage, { type: 'room-closed' }>>('room-closed', (message) => {
    game.quit();
    hud.hidden = true;
    showScreen('menu-screen');
    showToast(message.reason);
  });
  client.on('disconnected', () => {
    if (lastMode === 'online' && !$('#menu-screen').classList.contains('is-active')) {
      game.quit();
      hud.hidden = true;
      showScreen('menu-screen');
      showToast('Connessione alla sala macchine interrotta.');
    }
  });
  client.on('reconnecting', () => {
    showToast('Rete instabile: tento di riaprire la valvola per 25 secondi…');
  });
  client.on('reconnected', () => {
    showToast('Valvola di rete riaperta!');
  });
};

const ensureNetwork = async (): Promise<NetworkClient> => {
  if (network?.connected) return network;
  network = new NetworkClient();
  bindNetwork(network);
  await network.connect();
  return network;
};

const getNickname = (): string => {
  const value = ($('#nickname') as HTMLInputElement).value.trim() || 'Paperotto';
  localStorage.setItem('qod-nickname', value);
  return value;
};

const getPlayerColor = (): PlayerColor => {
  const value = ($('#player-color') as HTMLSelectElement).value as PlayerColor;
  const color = value in PLAYER_COLORS ? value : 'cyan';
  localStorage.setItem('qod-player-color', color);
  return color;
};

const setOnlineBusy = (busy: boolean): void => {
  const createButton = $('#create-room-btn') as HTMLButtonElement;
  const joinButton = $('#join-room-btn') as HTMLButtonElement;
  createButton.disabled = busy;
  joinButton.disabled = busy;
  $('#online-screen').setAttribute('aria-busy', String(busy));
  $('#online-status').textContent = busy ? 'Collegamento alla sala macchine…' : '';
};

const loopbackHost = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
$('#online-help').textContent = loopbackHost
  ? 'Per giocare da un altro computer, apri il gioco tramite l’indirizzo “Network” mostrato nel terminale invece di localhost.'
  : `Gli amici devono aprire ${location.origin} e inserire il codice della stanza.`;

const refreshHordeUnlock = (): void => {
  const unlocked = localStorage.getItem('qod-campaign-complete') === '1';
  const hordeButton = $('#horde-btn') as HTMLButtonElement;
  hordeButton.disabled = !unlocked;
  hordeButton.textContent = unlocked ? 'Modalità orde' : 'Modalità orde · completa la campagna';
  const hordeOption = ($('#online-mode') as HTMLSelectElement).querySelector<HTMLOptionElement>('option[value="horde"]');
  if (hordeOption) hordeOption.disabled = !unlocked;
};

$('#solo-btn').addEventListener('click', () => {
  lastMode = 'solo';
  lastPlayerCount = 1;
  lastGameMode = 'campaign';
  $('#network-pill').hidden = true;
  showGame();
  game.startSolo();
});

$('#horde-btn').addEventListener('click', () => {
  if (localStorage.getItem('qod-campaign-complete') !== '1') return;
  lastMode = 'solo';
  lastPlayerCount = 1;
  lastGameMode = 'horde';
  $('#network-pill').hidden = true;
  showGame();
  game.startSolo('horde');
});

$('#online-btn').addEventListener('click', () => showScreen('online-screen'));
$('#settings-btn').addEventListener('click', () => showScreen('settings-screen'));
const loadLeaderboard = async (): Promise<void> => {
  showScreen('leaderboard-screen');
  if (latestLeaderboard.length === 0) {
    const loading = document.createElement('li');
    loading.textContent = 'Collegamento alle tubature…';
    $('#leaderboard-list').replaceChildren(loading);
  }
  try {
    const client = await ensureNetwork();
    client.requestLeaderboard();
  } catch {
    const item = document.createElement('li');
    item.textContent = 'Classifica non raggiungibile. La sala macchine è offline.';
    $('#leaderboard-list').replaceChildren(item);
  }
};
$('#leaderboard-btn').addEventListener('click', () => { void loadLeaderboard(); });
$('#refresh-leaderboard-btn').addEventListener('click', () => { void loadLeaderboard(); });
for (const button of document.querySelectorAll<HTMLElement>('[data-back]')) {
  button.addEventListener('click', () => showScreen('menu-screen'));
}

$('#create-room-btn').addEventListener('click', async () => {
  $('#online-error').textContent = '';
  setOnlineBusy(true);
  try {
    const client = await ensureNetwork();
    selectedOnlineMode = ($('#online-mode') as HTMLSelectElement).value as GameMode;
    client.createRoom(getNickname(), getPlayerColor());
  } catch (error) {
    $('#online-error').textContent = error instanceof Error ? error.message : 'Errore di connessione.';
  } finally {
    setOnlineBusy(false);
  }
});

$('#join-room-btn').addEventListener('click', async () => {
  $('#online-error').textContent = '';
  const code = ($('#room-code') as HTMLInputElement).value.trim();
  if (code.length !== 5) {
    $('#online-error').textContent = 'Il codice stanza contiene 5 caratteri.';
    return;
  }
  setOnlineBusy(true);
  try {
    const client = await ensureNetwork();
    selectedOnlineMode = ($('#online-mode') as HTMLSelectElement).value as GameMode;
    client.joinRoom(getNickname(), code, getPlayerColor());
  } catch (error) {
    $('#online-error').textContent = error instanceof Error ? error.message : 'Errore di connessione.';
  } finally {
    setOnlineBusy(false);
  }
});

$('#start-online-btn').addEventListener('click', () => network?.startGame(settings.difficulty, selectedOnlineMode));
$('#leave-room-btn').addEventListener('click', () => {
  network?.leave();
  network = undefined;
  showScreen('menu-screen');
});

$('#resume-btn').addEventListener('click', () => {
  showGame();
  game.resume();
});

$('#quit-btn').addEventListener('click', () => {
  game.quit();
  hud.hidden = true;
  network?.leave();
  network = undefined;
  showScreen('menu-screen');
});

$('#restart-btn').addEventListener('click', () => {
  if (lastMode === 'online') network?.startGame(settings.difficulty, selectedOnlineMode);
  else {
    showGame();
    game.startSolo(lastGameMode);
  }
});

$('#result-menu-btn').addEventListener('click', () => {
  game.quit();
  network?.leave();
  network = undefined;
  showScreen('menu-screen');
});

const sensitivity = $('#sensitivity') as HTMLInputElement;
const volume = $('#volume') as HTMLInputElement;
const reducedMotion = $('#reduced-motion') as HTMLInputElement;
const difficulty = $('#difficulty') as HTMLSelectElement;
sensitivity.value = String(settings.sensitivity);
volume.value = String(settings.volume);
reducedMotion.checked = settings.reducedMotion;
difficulty.value = settings.difficulty;
$('#sensitivity-value').textContent = settings.sensitivity.toFixed(2);
$('#volume-value').textContent = `${Math.round(settings.volume * 100)}%`;
sensitivity.addEventListener('input', () => { $('#sensitivity-value').textContent = Number(sensitivity.value).toFixed(2); });
volume.addEventListener('input', () => { $('#volume-value').textContent = `${Math.round(Number(volume.value) * 100)}%`; });

$('#settings-screen').querySelector('form')?.addEventListener('submit', (event) => {
  event.preventDefault();
  settings = {
    sensitivity: Number(sensitivity.value),
    volume: Number(volume.value),
    reducedMotion: reducedMotion.checked,
    difficulty: difficulty.value as GameSettings['difficulty'],
  };
  localStorage.setItem('qod-settings', JSON.stringify(settings));
  game.updateSettings(settings);
  showScreen('menu-screen');
  showToast('Regolazioni salvate. Quack!');
});

$('#copy-invite-btn').addEventListener('click', async () => {
  const link = ($('#invite-link') as HTMLInputElement).value;
  try {
    await navigator.clipboard.writeText(link);
    showToast('Link d’invito copiato. Apri le valvole!');
  } catch {
    ($('#invite-link') as HTMLInputElement).select();
    showToast('Link selezionato: copialo con Ctrl/Cmd+C.');
  }
});

const nicknameInput = $('#nickname') as HTMLInputElement;
const playerColorSelect = $('#player-color') as HTMLSelectElement;
const storedPlayerColor = localStorage.getItem('qod-player-color');
const initialPlayerColor: PlayerColor = storedPlayerColor && storedPlayerColor in PLAYER_COLORS
  ? storedPlayerColor as PlayerColor
  : 'cyan';
nicknameInput.value = localStorage.getItem('qod-nickname') || 'Paperotto';
playerColorSelect.value = initialPlayerColor;
$('#build-version').textContent = packageInfo.version;
try {
  suitPreview = new SuitPreview($('#suit-preview-canvas') as HTMLCanvasElement, initialPlayerColor);
} catch {
  $('#suit-preview').classList.add('is-fallback');
  ($('#suit-preview-canvas') as HTMLCanvasElement).hidden = true;
  $('#suit-preview').querySelector('small')!.textContent = 'Anteprima 3D non disponibile; il colore resta visibile ai compagni.';
}
const updateSuitPreview = (): void => {
  const color = playerColorSelect.value as PlayerColor;
  const nickname = nicknameInput.value.trim() || 'Paperotto';
  suitPreview?.setColor(color);
  $('#suit-preview').dataset.color = color;
  $('#suit-preview-name').textContent = nickname;
  $('#suit-preview-label').textContent = PLAYER_SUIT_LABELS[color];
};
playerColorSelect.addEventListener('input', () => {
  localStorage.setItem('qod-player-color', playerColorSelect.value);
  updateSuitPreview();
});
nicknameInput.addEventListener('input', updateSuitPreview);
updateSuitPreview();
refreshHordeUnlock();
const invitedRoom = new URLSearchParams(location.search).get('room')?.trim().toUpperCase();
if (invitedRoom?.length === 5) {
  ($('#room-code') as HTMLInputElement).value = invitedRoom;
  showScreen('online-screen');
  showToast(`Invito ${invitedRoom} caricato: scegli il nome ed entra.`);
} else {
  showScreen('menu-screen');
}
